import { useEffect, useRef } from 'react'
import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { X, CloudDownload, CheckCircle2, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { usePrefetchStore } from '@/store/usePrefetchStore.ts'
import { getTopBannerOffsetPx } from '@/routes/root/view/topBannerLayout.ts'

const LIST_PHASE_WEIGHT = 20
const DETAIL_PHASE_WEIGHT = 50
const ATTACHMENT_PHASE_WEIGHT = 30

export function getProgressPercent(
  phase: string,
  completedIssueTypes: number,
  totalIssueTypes: number,
  completedIssues: number,
  totalIssues: number,
  completedAttachments: number,
  totalAttachments: number,
): number {
  if (phase === 'done') return 100

  if (phase === 'fetching-lists') {
    if (totalIssueTypes === 0) return 0
    return Math.round((completedIssueTypes / totalIssueTypes) * LIST_PHASE_WEIGHT)
  }

  if (phase === 'fetching-details') {
    if (totalIssues === 0) return LIST_PHASE_WEIGHT
    return LIST_PHASE_WEIGHT + Math.round((completedIssues / totalIssues) * DETAIL_PHASE_WEIGHT)
  }

  if (phase === 'fetching-attachments') {
    const detailDone = LIST_PHASE_WEIGHT + DETAIL_PHASE_WEIGHT
    if (totalAttachments === 0) return detailDone
    return (
      detailDone + Math.round((completedAttachments / totalAttachments) * ATTACHMENT_PHASE_WEIGHT)
    )
  }

  return 0
}

type PrefetchProgressBarProps = {
  bannerIndex?: number
}

export default function PrefetchProgressBar({ bannerIndex = 0 }: PrefetchProgressBarProps) {
  const { t } = useTranslation()
  const phase = usePrefetchStore(s => s.phase)
  const totalIssues = usePrefetchStore(s => s.totalIssues)
  const completedIssues = usePrefetchStore(s => s.completedIssues)
  const totalIssueTypes = usePrefetchStore(s => s.totalIssueTypes)
  const completedIssueTypes = usePrefetchStore(s => s.completedIssueTypes)
  const totalAttachments = usePrefetchStore(s => s.totalAttachments)
  const completedAttachments = usePrefetchStore(s => s.completedAttachments)
  const errorCount = usePrefetchStore(s => s.errorCount)
  const isVisible = usePrefetchStore(s => s.isVisible)
  const dismiss = usePrefetchStore(s => s.dismiss)

  const autoDismissRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (phase === 'done') {
      autoDismissRef.current = setTimeout(dismiss, 5000)
    }
    return () => {
      if (autoDismissRef.current) clearTimeout(autoDismissRef.current)
    }
  }, [phase, dismiss])

  if (!isVisible || phase === 'idle' || phase === 'cancelled') return null

  const percent = getProgressPercent(
    phase,
    completedIssueTypes,
    totalIssueTypes,
    completedIssues,
    totalIssues,
    completedAttachments,
    totalAttachments,
  )

  const isDone = phase === 'done'
  const hasErrors = errorCount > 0

  const getStatusText = () => {
    if (isDone) {
      return hasErrors
        ? t('prefetch.completeWithErrors', {
            count: completedIssues,
            attachments: completedAttachments,
            errors: errorCount,
          })
        : t('prefetch.complete', { count: completedIssues, attachments: completedAttachments })
    }
    if (phase === 'fetching-lists') {
      return t('prefetch.fetchingLists', { current: completedIssueTypes, total: totalIssueTypes })
    }
    if (phase === 'fetching-attachments') {
      return t('prefetch.fetchingAttachments', {
        current: completedAttachments,
        total: totalAttachments,
      })
    }
    return t('prefetch.fetchingDetails', { current: completedIssues, total: totalIssues })
  }

  const statusText = getStatusText()

  const StatusIcon = isDone ? (hasErrors ? AlertTriangle : CheckCircle2) : CloudDownload

  return (
    <div
      className={cn(
        'fixed left-0 right-0 z-[110]',
        'top-[calc(var(--safe-area-inset-top)+var(--top-banner-offset,0px))]',
        'md:top-[var(--top-banner-offset,0px)]',
        'md:left-[var(--sidebar-width)]',
      )}
      style={
        {
          '--top-banner-offset': `${getTopBannerOffsetPx({ bannerIndex })}px`,
        } as CSSProperties
      }
    >
      {/* Matches other top banners: 32px single-line row. Progress is shown
          as a 2px line pinned to the bottom of the banner, overlapping the
          border so the banner footprint stays h-8 and fits the reserved
          slot. Status text truncates; percent sits on the right next to
          the dismiss button (when done). */}
      <div
        className={cn(
          'relative flex h-8 items-center gap-2 border-b px-3',
          isDone &&
            !hasErrors &&
            'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/50',
          isDone &&
            hasErrors &&
            'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/50',
          !isDone && 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/50',
        )}
      >
        <StatusIcon
          className={cn(
            'size-3.5 shrink-0',
            isDone && !hasErrors && 'text-green-600 dark:text-green-400',
            isDone && hasErrors && 'text-amber-600 dark:text-amber-400',
            !isDone && 'animate-pulse text-blue-600 dark:text-blue-400',
          )}
        />

        <span
          className={cn(
            'min-w-0 flex-1 truncate text-xs font-medium',
            isDone && !hasErrors && 'text-green-800 dark:text-green-200',
            isDone && hasErrors && 'text-amber-800 dark:text-amber-200',
            !isDone && 'text-blue-800 dark:text-blue-200',
          )}
        >
          {statusText}
        </span>

        <span
          className={cn(
            'shrink-0 text-xs font-semibold tabular-nums',
            isDone && !hasErrors && 'text-green-600 dark:text-green-400',
            isDone && hasErrors && 'text-amber-600 dark:text-amber-400',
            !isDone && 'text-blue-600 dark:text-blue-400',
          )}
        >
          {percent}%
        </span>

        {isDone && (
          <button
            type="button"
            onClick={dismiss}
            aria-label={t('common.cancel')}
            className={cn(
              'shrink-0 rounded-md p-0.5 transition-colors',
              hasErrors
                ? 'text-amber-500 hover:bg-amber-200 hover:text-amber-700 dark:hover:bg-amber-900'
                : 'text-green-500 hover:bg-green-200 hover:text-green-700 dark:hover:bg-green-900',
            )}
          >
            <X className="size-3.5" />
          </button>
        )}

        {/* Thin progress line pinned to bottom of the banner. Overlaps the
            border-b so the banner footprint stays exactly h-8. */}
        <div
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={statusText}
          className="absolute inset-x-0 bottom-0 h-0.5"
        >
          <div
            className={cn(
              'h-full transition-all duration-300',
              isDone && !hasErrors && 'bg-green-500',
              isDone && hasErrors && 'bg-amber-500',
              !isDone && 'bg-blue-500',
            )}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    </div>
  )
}
